// Searchable/filterable list of individual asbestos records (zones) across every
// building — companion "All records" view alongside the by-building register.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, FileText, Camera, History, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useView } from "@/context/ViewContext";
import { RISK_COLOR, STATUS_LABEL } from "./canvas/constants";

const ACCENT = "#C8632A";

export default function AsbestosRecordsBrowser() {
    const navigate = useNavigate();
    const { portalClientId, isInternalView } = useView();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [buildingFilter, setBuildingFilter] = useState("");
    const [riskFilter, setRiskFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    const load = useCallback(() => {
        setLoading(true);
        const params = {};
        if (portalClientId && !isInternalView) params.client_id = portalClientId;
        api.get("/asbestos/records", { params })
            .then((r) => setRows(r.data || []))
            .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Could not load asbestos records"))
            .finally(() => setLoading(false));
    }, [portalClientId, isInternalView]);
    useEffect(load, [load]);

    const buildings = useMemo(() => {
        const map = new Map();
        rows.forEach((r) => { if (!map.has(r.building_id)) map.set(r.building_id, r.building_name); });
        return Array.from(map, ([id, name]) => ({ id, name }));
    }, [rows]);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        return rows.filter((r) => {
            if (buildingFilter && r.building_id !== buildingFilter) return false;
            if (riskFilter && r.risk_rating !== riskFilter) return false;
            if (statusFilter && r.status !== statusFilter) return false;
            if (term) {
                const hay = [r.label, r.reference_id, r.material_type, r.room, r.specific_location, r.building_name]
                    .filter(Boolean).join(" ").toLowerCase();
                if (!hay.includes(term)) return false;
            }
            return true;
        });
    }, [rows, q, buildingFilter, riskFilter, statusFilter]);

    const openRecord = (r) => navigate(`/app/buildings/${r.building_id}/asbestos?floor=${r.floor_id}&zone=${r.zone_id}`);
    const fmt = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }); } catch { return s; } };
    const clearFilters = () => { setQ(""); setBuildingFilter(""); setRiskFilter(""); setStatusFilter(""); };
    const hasFilters = q || buildingFilter || riskFilter || statusFilter;

    return (
        <div className="space-y-4" data-testid="asbestos-records-browser">
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[200px] max-w-xs">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-[#9A968F]" />
                    <input data-testid="asbestos-records-search" value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder="Search reference, room, material…"
                        className="w-full h-9 pl-8 pr-3 rounded-lg border text-[12.5px]" style={{ borderColor: "#E5E0DA" }} />
                </div>
                <select data-testid="asbestos-records-filter-building" value={buildingFilter} onChange={(e) => setBuildingFilter(e.target.value)}
                    className="h-9 rounded-lg border text-[12.5px] px-2.5 bg-white" style={{ borderColor: "#E5E0DA" }}>
                    <option value="">All buildings</option>
                    {buildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                <select data-testid="asbestos-records-filter-risk" value={riskFilter} onChange={(e) => setRiskFilter(e.target.value)}
                    className="h-9 rounded-lg border text-[12.5px] px-2.5 bg-white" style={{ borderColor: "#E5E0DA" }}>
                    <option value="">All risk ratings</option>
                    {["High", "Medium", "Low"].map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <select data-testid="asbestos-records-filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-9 rounded-lg border text-[12.5px] px-2.5 bg-white" style={{ borderColor: "#E5E0DA" }}>
                    <option value="">All statuses</option>
                    <option value="red">Present</option>
                    <option value="green">None</option>
                    <option value="unknown">Pending</option>
                </select>
                {hasFilters && (
                    <button data-testid="asbestos-records-clear" onClick={clearFilters}
                        className="h-9 px-2.5 rounded-lg border text-[12px] inline-flex items-center gap-1" style={{ borderColor: "#E5E0DA", color: "#6F6A64" }}>
                        <X className="w-3 h-3" /> Clear
                    </button>
                )}
                <span className="text-[11.5px] ml-auto" style={{ color: "#9A968F" }}>{filtered.length} record{filtered.length === 1 ? "" : "s"}</span>
            </div>

            {loading ? (
                <div className="font-serif-italic text-[#9A968F] py-12 text-center">Loading records…</div>
            ) : filtered.length === 0 ? (
                <div data-testid="asbestos-records-empty" className="bg-white border rounded-[13px] py-14 text-center" style={{ borderColor: "#E5E0DA" }}>
                    <p className="text-[13.5px] font-semibold text-[#1F1F1F]">No records match</p>
                    <p className="text-[12px] mt-1" style={{ color: "#6F6A64" }}>{rows.length === 0 ? "No individual asbestos records have been placed on any floor plan yet." : "Try adjusting your filters."}</p>
                </div>
            ) : (
                <div className="bg-white border rounded-[13px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                    <div className="overflow-x-auto">
                        <table className="w-full text-[12.3px]" data-testid="asbestos-records-table">
                            <thead>
                                <tr className="bg-[#F6F4F0] text-[10.3px] uppercase tracking-wider" style={{ color: "#6F6A64" }}>
                                    <th className="text-left px-4 py-2.5 font-bold">Reference / Label</th>
                                    <th className="text-left px-3 py-2.5 font-bold">Building</th>
                                    <th className="text-left px-3 py-2.5 font-bold">Room / Location</th>
                                    <th className="text-left px-3 py-2.5 font-bold">Type</th>
                                    <th className="text-left px-3 py-2.5 font-bold">Condition</th>
                                    <th className="text-left px-3 py-2.5 font-bold">Risk</th>
                                    <th className="text-left px-3 py-2.5 font-bold">Status</th>
                                    <th className="text-left px-3 py-2.5 font-bold">Survey date</th>
                                    <th className="text-center px-3 py-2.5 font-bold">Files</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((r) => (
                                    <tr key={r.zone_id} data-testid={`asbestos-record-row-${r.zone_id}`}
                                        onClick={() => openRecord(r)}
                                        className="border-t hover:bg-[#FCFBF9] transition-colors cursor-pointer" style={{ borderColor: "#F0EDE8" }}>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1.5 font-semibold text-[#1F1F1F]">
                                                {r.reference_id || r.label || "(unlabelled)"} <ArrowUpRight className="w-3 h-3 text-[#C7C2BB]" />
                                            </div>
                                            {r.reference_id && r.label && <div className="text-[11px]" style={{ color: "#9A968F" }}>{r.label}</div>}
                                        </td>
                                        <td className="px-3 py-3 text-[#6F6A64]">{r.building_name}<div className="text-[10.5px]" style={{ color: "#9A968F" }}>{r.floor_name}</div></td>
                                        <td className="px-3 py-3 text-[#6F6A64]">{[r.room, r.specific_location].filter(Boolean).join(" · ") || "—"}</td>
                                        <td className="px-3 py-3 text-[#6F6A64]">{r.material_type || r.asbestos_type || "—"}</td>
                                        <td className="px-3 py-3 text-[#6F6A64]">{r.condition || "—"}</td>
                                        <td className="px-3 py-3">
                                            {r.risk_rating ? (
                                                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full text-white" style={{ background: RISK_COLOR[r.risk_rating] }}>{r.risk_rating}</span>
                                            ) : <span className="text-[#9A968F]">—</span>}
                                        </td>
                                        <td className="px-3 py-3 text-[#6F6A64]">{STATUS_LABEL[r.status] || r.status}</td>
                                        <td className="px-3 py-3 text-[#6F6A64]">{fmt(r.survey_date)}</td>
                                        <td className="px-3 py-3">
                                            <div className="flex items-center justify-center gap-2.5 text-[10.5px]" style={{ color: "#9A968F" }}>
                                                {r.photos_count > 0 && <span className="inline-flex items-center gap-0.5"><Camera className="w-3 h-3" />{r.photos_count}</span>}
                                                {r.documents_count > 0 && <span className="inline-flex items-center gap-0.5"><FileText className="w-3 h-3" />{r.documents_count}</span>}
                                                {r.inspections_count > 0 && <span className="inline-flex items-center gap-0.5"><History className="w-3 h-3" />{r.inspections_count}</span>}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
